import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';

import ButtonToggle from '../../fragments/button/ButtonToggle';
import Button from '../../fragments/button/Button';
import IconUI from '../../fragments/icon/IconUI';

// Number of term pills shown before the "+ See more" reveal.
const VISIBLE_TERM_LIMIT = 7;

// Filter groups shown to visitors, dropping the "role" taxonomy (e.g.
// "staff-role", "board-members-role") — not a useful filter. Shared with the
// parent CardGrid so it can tell whether a filter bar will render.
export const getVisibleFilters = (filters = []) =>
  filters.filter((group) => !/(^|-)role$/.test(group.taxonomy));

// A single collapsible taxonomy group: header + animated body of toggle pills.
function FilterGroup({
  group,
  selected,
  onToggleTerm,
  onSelectAll,
  defaultOpen,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  const selectedTerms = selected[group.taxonomy] || [];
  const allSelected = selectedTerms.length === 0;

  const terms = showAll
    ? group.terms
    : group.terms.slice(0, VISIBLE_TERM_LIMIT);
  const hasMore = group.terms.length > VISIBLE_TERM_LIMIT;

  return (
    <div className={`CardGridFilters-group ${open ? 'is-open' : ''}`}>
      <button
        type="button"
        className="CardGridFilters-groupHeader"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        <span className="CardGridFilters-groupLabel headingStrong step--1">
          {group.label}
        </span>
        <IconUI icon="chevronDown" />
      </button>

      <div className="CardGridFilters-groupBody" aria-hidden={!open}>
        <div className="CardGridFilters-groupBodyInner">
          <div className="CardGridFilters-pills">
            <ButtonToggle
              buttonLabel="All"
              isSelected={allSelected}
              onClick={() => onSelectAll(group.taxonomy)}
            />
            {terms.map((term) => (
              <ButtonToggle
                key={term.slug}
                buttonLabel={term.name}
                isSelected={selectedTerms.includes(term.slug)}
                onClick={() => onToggleTerm(group.taxonomy, term.slug)}
              />
            ))}
          </div>

          {hasMore && (
            <button
              type="button"
              className="CardGridFilters-seeMore"
              onClick={() => setShowAll((prev) => !prev)}
            >
              <IconUI icon={showAll ? 'minus' : 'plus'} />
              <span>{showAll ? 'See less' : 'See more'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Shared list of taxonomy groups, rendered in both the desktop sidebar and the
// mobile drawer.
function FilterPanel({ filters, selected, onToggleTerm, onSelectAll }) {
  return (
    <div className="CardGridFilters-groups">
      {filters.map((group, index) => (
        <FilterGroup
          key={group.taxonomy}
          group={group}
          selected={selected}
          onToggleTerm={onToggleTerm}
          onSelectAll={onSelectAll}
          defaultOpen={index === 0}
        />
      ))}
    </div>
  );
}

export default function CardGridFilters({
  filters = [],
  isInView = false,
  selected,
  onToggleTerm,
  onSelectAll,
  onClearAll,
}) {
  const [open, setOpen] = useState(false);

  // The trigger and drawer are portalled to <body> (see below), which is
  // client-only — hold them back until after mount so SSR and hydration match.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Lock background scroll while the mobile drawer is open.
  useEffect(() => {
    if (open) {
      window.lenis?.stop();
    } else {
      window.lenis?.start();
    }
    return () => window.lenis?.start();
  }, [open]);

  const visibleFilters = getVisibleFilters(filters);

  if (!visibleFilters.length) return null;

  const panelProps = {
    filters: visibleFilters,
    selected,
    onToggleTerm,
    onSelectAll,
  };

  const selectedCount = Object.values(selected).reduce(
    (total, terms) => total + (terms?.length || 0),
    0
  );
  const filtersTitle =
    selectedCount > 0
      ? `Filters (${String(selectedCount).padStart(2, '0')})`
      : 'Filters';

  return (
    <>
      {/* Desktop: sticky sidebar */}
      <aside className="CardGridFilters CardGridFilters--desktop">
        <p className="CardGridFilters-title headingStrong">{filtersTitle}</p>
        <FilterPanel {...panelProps} />
      </aside>

      {/* Mobile: floating trigger + bottom-sheet drawer, both portalled to
          <body>. They're viewport-fixed, and PageTransition's
          `.contentContainer` wrapper carries `will-change: transform` plus a
          residual Motion transform — that makes it the containing block for
          fixed descendants, so in place the trigger lands at the bottom of the
          page and the drawer scrim stretches over the whole document. Same
          escape hatch as StaffDeepDive / VideoLightbox. Do not inline these.
          `isInView` (an IntersectionObserver on the grid in CardGrid.jsx) is
          what keeps the trigger from floating over the rest of the page. */}
      {mounted &&
        createPortal(
          <>
            <Button
              className={`CardGridFilters-trigger${
                isInView ? ' is-visible' : ''
              }`}
              buttonLabel="Filters"
              theme="primary"
              icon="filterLines"
              tabIndex={isInView ? undefined : -1}
              aria-hidden={isInView ? undefined : true}
              onClick={() => setOpen(true)}
            />

            <AnimatePresence>
              {open && (
                <motion.div
                  className="CardGridFilters-overlay"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.25, ease: 'easeOut' }}
                >
                  <div
                    className="CardGridFilters-scrim"
                    onClick={() => setOpen(false)}
                  />
                  <motion.div
                    className="CardGridFilters CardGridFilters--drawer"
                    initial={{ y: '100%' }}
                    animate={{ y: '0%' }}
                    exit={{ y: '100%' }}
                    transition={{ duration: 0.35, ease: 'easeInOut' }}
                  >
                    <div className="CardGridFilters-drawerHead">
                      <p className="CardGridFilters-title headingStrong">
                        {filtersTitle}
                      </p>
                      <button
                        type="button"
                        className="CardGridFilters-close"
                        aria-label="Close filters"
                        onClick={() => setOpen(false)}
                      >
                        <IconUI icon="close" />
                      </button>
                    </div>

                    <div className="CardGridFilters-drawerBody">
                      <FilterPanel {...panelProps} />
                    </div>

                    <div className="CardGridFilters-drawerFoot">
                      <Button
                        buttonLabel="Clear all"
                        theme="secondary"
                        icon={false}
                        onClick={onClearAll}
                      />
                      <Button
                        buttonLabel="Apply"
                        theme="primary"
                        icon={false}
                        onClick={() => setOpen(false)}
                      />
                    </div>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </>,
          document.body
        )}
    </>
  );
}
